'use client';
import classNames from 'classnames';
import { roleLinks } from 'components/layout/NavBar/constants';
import { openNavBarFn } from 'components/layout/NavBar/helpers';
import { LinkItem } from 'components/layout/NavBar/types';
import SupportSvg from 'components/svg/SupportSvg';
import Button from 'components/ui/Button';
import P from 'components/ui/P';
import { useIndexPageContext } from 'context/index_page_state';
import { useAppContext } from 'context/state';
import { useUserContext } from 'context/user_state';
import { ItemsNotificationType } from 'data/interfaces/INotification';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { SalesBanners } from 'types/enums';
import styles from './index.module.scss';
import { TELEGRAM_SUPPORT_LINK } from 'types/constants';

const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const userContext = useUserContext();
  const t = useTranslations();
  const profileType = userContext.currentProfile?.profileType;
  const indexPageContext = useIndexPageContext();
  const appContext = useAppContext();

  const activePath = router.pathname;

  const toggleNavBarHandler = () => {
    if (isOpen) {
      setIsOpen(false);
      openNavBarFn(false);
    } else {
      setIsOpen(true);
      openNavBarFn(true);
    }
  };

  const getNotificationCount = (type: ItemsNotificationType): number => {
    return indexPageContext.unreadStats.find((el) => el.type === type)?.count;
  };

  if (!userContext.currentProfile) {
    return null;
  }

  const getIconHelper = (item: LinkItem): React.JSX.Element => {
    if (!item.notificationType) return item.icon;
    return getNotificationCount(item.notificationType) > 0 ? item.iconActive : item.icon;
  };

  return (
    <div className={classNames(styles.root, { [styles.root__open]: isOpen })} id='root-navbar'>
      <div
        className={classNames(styles.wrapper, {
          [styles.wrapper__open]: isOpen,
          [styles.wrapper__withTopBanner]:
            appContext?.banners &&
            appContext?.banners?.find(
              (el) => el.position.toUpperCase() === SalesBanners.Top.toUpperCase(),
            ),
        })}
        id='navbar-wrapper'>
        <Link href={'/'} className={classNames(styles.logo, { [styles.logo__open]: isOpen })}>
          <Image src={'/icons/logo.svg'} alt={'logo'} width={26} height={26} />
          <div className={styles.linkText}>
            <Image src={'/icons/logoTitle.svg'} alt={'logoTitle'} width={65} height={10} />
          </div>
        </Link>

        {roleLinks[profileType]?.map((el, i) => {
          return (
            <Link
              aria-label='to chats'
              className={classNames(styles.link, styles.linkPage, {
                [styles.link__open]: isOpen,
                [styles.link__selected]: activePath === el.path,
              })}
              href={{ pathname: el?.path, query: el?.query }}
              key={el.path + i}>
              <Button
                className={classNames(styles.buttonIcon, {
                  [styles.buttonIcon__selected]: activePath === el.path,
                })}
                altStyleType={['icon']}
                styleType={['icon']}>
                {getIconHelper(el)}
              </Button>
              <P
                styleType={['w500', 'medium']}
                useThemeColor
                className={classNames(styles.linkText, styles.linkPageText, {
                  [styles.linkPageText__selected]: activePath === el.path,
                })}>
                {el.isRawText ? el.text : t(el.text)}
              </P>
            </Link>
          );
        })}

        <Link
          href={{ pathname: TELEGRAM_SUPPORT_LINK }}
          className={classNames(styles.link, styles.linkSupport, styles.linkPage, {
            [styles.link__open]: isOpen,
          })}
          target='_blank'
          rel='noopener noreferrer'>
          <Button className={styles.buttonIcon} altStyleType={['icon']} styleType={['icon']}>
            <SupportSvg />
          </Button>
          <P
            styleType={['w500', 'medium']}
            useThemeColor
            className={classNames(styles.linkText, styles.linkPageText)}>
            {t('nav_support')}
          </P>
        </Link>

        <Button
          className={classNames(styles.toggleArrow, { [styles.toggleArrow__open]: isOpen })}
          onClick={toggleNavBarHandler}
          styleType={['icon']}
          altStyleType={['icon']}>
          {isOpen ? (
            <Image
              src='/icons/ChevronsLeftWide.svg'
              alt='ChevronsLeftWide'
              width={16}
              height={16}
            />
          ) : (
            <Image
              src='/icons/ChevronsRightWide.svg'
              alt='ChevronsRightWide'
              width={16}
              height={16}
            />
          )}
        </Button>
      </div>
    </div>
  );
};

export default NavBar;
